import { Box, InputBase, Popover, Typography } from '@mui/material';
import type { PlannerAllocation, ResourceSearchItem } from '@timelog/pm-shared';
import type { KeyboardEvent } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useResolveGeneric, useRpResourceSearch } from '@/api';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { MicroLabel, MonoText } from '@/components/ui';
import { formatHours, useDebouncedValue } from '@/lib';
import { stableColor } from '@/lib/roleColor';

export type MoveHoursPopoverProps = {
  /** The generic slot being resolved; null keeps the popover closed. */
  allocation: PlannerAllocation | null;
  roleName: string;
  anchor: HTMLElement | null;
  onClose: () => void;
  onMoved: (message: string) => void;
};

/** Connection strength to the slot's competency: 2 primary, 1 secondary, 0 none. */
const connectionTier = (item: ResourceSearchItem, roleId: string | null): number => {
  const entry = item.roles.find((role) => role.roleId === roleId);
  return entry ? (entry.isPrimary ? 2 : 1) : 0;
};

const TIER_KEYS = {
  2: 'tier.Primary',
  1: 'tier.Secondary',
  0: 'tier.Other',
} as const satisfies Record<number, string>;

const tierKey = (tier: number) => TIER_KEYS[tier === 2 ? 2 : tier === 1 ? 1 : 0];

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0]!.toUpperCase())
    .slice(0, 2)
    .join('');

/**
 * Move competency hours to a named person (the design's Move flow, backed by
 * POST /rp/allocations/:id/resolve — DL-031: the envelope moves, bookings
 * stay on the slot). The picker ranks by connection tier; the default list is
 * connected people only, typing opens it to everyone.
 */
export const MoveHoursPopover = ({
  allocation,
  roleName,
  anchor,
  onClose,
  onMoved,
}: MoveHoursPopoverProps) => {
  const { t } = useTranslation(['pmResources', 'pmCommon']);
  const open = allocation !== null && anchor !== null;
  const [amount, setAmount] = useState('');
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const debounced = useDebouncedValue(query);
  const search = useRpResourceSearch(debounced, open);
  const resolve = useResolveGeneric();

  const candidates = useMemo(() => {
    if (!allocation) return [];
    const items = (search.data?.items ?? [])
      .map((item) => ({ item, tier: connectionTier(item, allocation.roleId) }))
      // Connected-only until the user searches; a query opens the list.
      .filter(({ tier }) => (debounced.trim() === '' ? tier > 0 : true));
    items.sort((a, b) => b.tier - a.tier || a.item.name.localeCompare(b.item.name));
    return items;
  }, [search.data, allocation, debounced]);

  const highlighted = candidates[Math.min(highlight, Math.max(0, candidates.length - 1))];
  const fullAmount = allocation ? formatHours(Math.round(allocation.allocatedBudget)) : '';

  const reset = () => {
    setAmount('');
    setQuery('');
    setHighlight(0);
    setError(null);
  };

  const close = () => {
    reset();
    onClose();
  };

  const commit = (target: ResourceSearchItem | undefined) => {
    if (!allocation || !target || resolve.isPending) return;
    const parsed = Number(amount.replace(',', '.'));
    const hours =
      Number.isFinite(parsed) && parsed > 0
        ? Math.min(parsed, allocation.allocatedBudget)
        : allocation.allocatedBudget;
    resolve.mutate(
      {
        allocationId: allocation.allocationId,
        resourceId: target.resourceId,
        hoursToResolve: hours,
      },
      {
        onSuccess: () => {
          const remainder = allocation.allocatedBudget - hours;
          const moved = { hours: formatHours(hours), role: roleName, name: target.name };
          onMoved(
            remainder > 0.001
              ? t('moveHours.MovedToastWithRemainder', {
                  ...moved,
                  remainder: formatHours(remainder),
                })
              : t('moveHours.MovedToast', moved),
          );
          close();
        },
        onError: (mutationError) => {
          setError(mutationError.message);
        },
      },
    );
  };

  const onSearchKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlight((current) => Math.min(current + 1, candidates.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      commit(highlighted?.item);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      close();
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchor}
      onClose={close}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      slotProps={{ paper: { sx: { width: 344 } } }}
      data-automation-id="move-hours-popover"
    >
      <Box
        sx={{
          p: '12px 14px 10px',
          borderBottom: 'var(--border-width-thin) solid var(--border-subtle)',
        }}
      >
        <Typography variant="bodyBold">{t('moveHours.Title')}</Typography>
        <Typography variant="label" component="div" sx={{ color: 'var(--fg-3)', mt: 0.25 }}>
          {allocation
            ? t('moveHours.Subtitle', {
                role: roleName,
                project: allocation.projectNumber ?? allocation.projectName,
                hours: fullAmount,
              })
            : ''}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          columnGap: '8px',
          p: '11px 14px',
          borderBottom: 'var(--border-width-thin) solid var(--border-subtle)',
        }}
      >
        <Typography variant="label" sx={{ color: 'var(--fg-2)' }}>
          {t('moveHours.MoveLabel')}
        </Typography>
        <InputBase
          value={amount}
          placeholder={fullAmount}
          onChange={(event) => setAmount(event.target.value)}
          inputProps={{
            'data-automation-id': 'move-amount-input',
            inputMode: 'decimal',
            style: { textAlign: 'right' },
          }}
          sx={{
            width: 80,
            height: 28,
            px: 1,
            border: 'var(--border-width-thin) solid var(--color-blue-80)',
            borderRadius: 'var(--border-radius-small)',
            fontFamily: 'var(--font-family-mono)',
            fontSize: 11,
          }}
        />
        <Typography variant="label" sx={{ color: 'var(--fg-2)' }}>
          {t('pmCommon:unit.HoursShort')}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Box
          component="button"
          type="button"
          data-automation-id="move-all"
          onClick={() => setAmount(allocation ? String(allocation.allocatedBudget) : '')}
          sx={{
            border: 0,
            background: 'transparent',
            cursor: 'pointer',
            p: 0,
            color: 'var(--color-pink-80)',
            '&:hover': { textDecoration: 'underline' },
          }}
        >
          <Typography variant="label" sx={{ color: 'inherit' }}>
            {t('moveHours.MoveAll')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ p: '10px 10px 4px' }}>
        <InputBase
          autoFocus
          fullWidth
          value={query}
          placeholder={t('moveHours.SearchPlaceholder')}
          onChange={(event) => {
            setQuery(event.target.value);
            setHighlight(0);
          }}
          onKeyDown={onSearchKey}
          inputProps={{ 'data-automation-id': 'move-search-input' }}
          sx={{
            height: 30,
            px: 1,
            border: 'var(--border-width-thin) solid var(--border-subtle)',
            borderRadius: 'var(--border-radius-small)',
            fontSize: 14,
          }}
        />
      </Box>

      <Box sx={{ maxHeight: 230, overflowY: 'auto', p: '2px 6px 6px' }}>
        {candidates.map(({ item, tier }, index) => {
          const header =
            index === 0 || candidates[index - 1]!.tier !== tier ? (
              <Box key={`tier-${String(tier)}`} sx={{ p: '8px 8px 3px' }}>
                <MicroLabel color="var(--fg-3)">{t(tierKey(tier))}</MicroLabel>
              </Box>
            ) : null;
          return (
            <Box key={item.resourceId}>
              {header}
              <Box
                data-automation-id="move-candidate"
                onMouseDown={(event) => {
                  event.preventDefault();
                  commit(item);
                }}
                onMouseEnter={() => setHighlight(index)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  columnGap: '9px',
                  p: '6px 8px',
                  borderRadius: 'var(--border-radius-medium)',
                  cursor: 'pointer',
                  bgcolor: index === highlight ? 'var(--bg-hover)' : 'transparent',
                }}
              >
                <Box
                  aria-hidden
                  sx={{
                    width: 25,
                    height: 25,
                    flex: 'none',
                    borderRadius: 'var(--border-radius-pill)',
                    bgcolor: `color-mix(in srgb, ${stableColor(item.resourceId)} 24%, var(--bg-panel))`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="label">{item.initials ?? initialsOf(item.name)}</Typography>
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="bodyRegular"
                    component="div"
                    sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {item.name}
                  </Typography>
                  <Typography variant="label" component="div" sx={{ color: 'var(--fg-3)' }}>
                    {item.position ?? item.roles.find((role) => role.isPrimary)?.roleName ?? ''}
                  </Typography>
                </Box>
                {tier === 2 && (
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      columnGap: '3px',
                      flex: 'none',
                    }}
                  >
                    <MaterialSymbol name="star" size={14} color="var(--color-pink-80)" filled />
                    <Typography variant="label" sx={{ color: 'var(--color-pink-80)' }}>
                      {t('moveHours.PrimaryBadge')}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
        {candidates.length === 0 && (
          <Typography variant="label" component="div" sx={{ color: 'var(--fg-3)', p: '10px 9px' }}>
            {debounced.trim() === '' ? t('moveHours.EmptyConnected') : t('moveHours.NoMatches')}
          </Typography>
        )}
        {error && (
          <Typography
            variant="label"
            component="div"
            data-automation-id="move-error"
            sx={{ color: 'var(--color-coral-100)', p: '6px 9px' }}
          >
            {error}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          columnGap: '14px',
          p: '8px 13px 9px',
          borderTop: 'var(--border-width-thin) solid var(--border-subtle)',
        }}
      >
        <MonoText size="label" color="var(--fg-3)">
          {t('moveHours.HintMove')}
        </MonoText>
        <MonoText size="label" color="var(--fg-3)">
          {t('moveHours.HintCancel')}
        </MonoText>
      </Box>
    </Popover>
  );
};
